/**
 * Allowlist for URLs taken from authored data (record fields, nav records) before they reach an
 * `href`. A `javascript:` href runs in the app origin on click, where the Keycloak tokens live, and
 * the CSP does not stop it (`script-src` carries `'unsafe-inline'`). Denylisting schemes misses
 * case, leading control characters and `data:` / `vbscript:`, so values are parsed with the
 * browser's own URL parser and only web schemes and same-origin paths pass.
 */

const WEB_PROTOCOLS: ReadonlySet<string> = new Set(["http:", "https:"]);

/** Any origin works as the base for relative-path resolution outside a browser. */
const FALLBACK_ORIGIN = "http://localhost";

const currentOrigin = (): string =>
  typeof window === "undefined" ? FALLBACK_ORIGIN : window.location.origin;

/** True for an absolute `http:` / `https:` URL. Relative paths are `false`. */
export const isSafeHttpUrl = (raw: string): boolean => {
  try {
    return WEB_PROTOCOLS.has(new URL(raw).protocol);
  } catch {
    return false;
  }
};

/**
 * True for a root-relative path that stays on this origin: one leading `/`, not `//` or `/\`
 * (both protocol-relative to a browser). The resolved-origin check also catches the parser
 * stripping tabs and newlines, which turns `/\t/evil.example` into `//evil.example`.
 */
export const isSafeRelativePath = (raw: string): boolean => {
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) {
    return false;
  }

  const origin = currentOrigin();

  try {
    return new URL(raw, origin).origin === origin;
  } catch {
    return false;
  }
};

/** True when `raw` is safe to render as an `href`: an http(s) URL or a same-origin path. */
export const isSafeHref = (raw: string): boolean => isSafeHttpUrl(raw) || isSafeRelativePath(raw);

/**
 * Same-origin path for navigation after {@link isSafeRelativePath} validation.
 * Falls back to `/` when the value is not a safe root-relative path.
 */
export const safeSameOriginPath = (raw: string): string => {
  if (!isSafeRelativePath(raw)) {
    return "/";
  }

  try {
    const parsed = new URL(raw, currentOrigin());
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return "/";
  }
};

/**
 * Full-page navigation for session return URLs and composed record links.
 * Uses {@link isSafeRelativePath} before assigning `location`.
 */
export const replaceLocationWithSameOriginPath = (raw: string): void => {
  // Pass only {@link safeSameOriginPath} output to `location.replace` so CodeQL's
  // html-injection barrier on that return value applies (see safe-url-barriers.json).
  window.location.replace(safeSameOriginPath(raw));
};
